import React from 'react'
import FormField from '@/components/common/FormField'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { errorTypes } from '@/utility/validation'
import { useFormContext } from 'react-hook-form'
import type { OnboardTabProps } from './onboard.types'
import states from '@/utility/states.json'

const OnboardAddress: React.FC<OnboardTabProps> = ({ isLoading, handleNextStep }) => {
    const methods = useFormContext()
    const { register, watch, setValue, formState: { errors } } = methods

    const isSameAsPermanentAddress = watch('isSameAsPermanentAddress')
    const presentAddress = watch('address')
    const presentState = watch('state')
    const presentCity = watch('city')
    const presentPinCode = watch('pinCode')

    // Automatically sync Permanent Address fields with Present Address when checked
    React.useEffect(() => {
        if (isSameAsPermanentAddress) {
            setValue('permanentAddress', presentAddress || '')
            setValue('permanentState', presentState || '')
            setValue('permanentCity', presentCity || '')
            setValue('permanentPinCode', presentPinCode || '')
        }
    }, [isSameAsPermanentAddress, presentAddress, presentState, presentCity, presentPinCode, setValue])

    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                    {...register('address', {
                        ...errorTypes.required('Address'),
                    })}
                    error={errors.address?.message as string}
                    label="Address"
                    id="Address"
                    placeholder="Enter address"
                    defaultValue={""}
                    required
                />
                <FormField
                    {...register('state', {
                        ...errorTypes.required('State'),
                    })}
                    error={errors.state?.message as string}
                    type="select"
                    label="State"
                    value={watch('state')}
                    placeholder="Enter state"
                    options={states}
                />
                <FormField
                    {...register('city', {
                        ...errorTypes.required('City'),
                    })}
                    error={errors.city?.message as string}
                    label="City"
                    id="city"
                    placeholder="Enter city"
                />
                <FormField
                    {...register('pinCode', {
                        ...errorTypes.required('Pin code'),
                        ...errorTypes.pinCode,
                    })}
                    error={errors.pinCode?.message as string}
                    label="Pin Code"
                    placeholder="Enter pin code"
                    type="text"
                />
            </div>
            <div className="my-3 border-b border-gray-200 pb-3 flex items-center justify-between">
                <h4 className="text-lg font-semibold">
                    Permanent Address
                </h4>
                <div className="form-check">
                    <FormField
                        {...register('isSameAsPermanentAddress')}
                        type="checkbox"
                        label="Same as present address"
                    />
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                    {...register('permanentAddress', {
                        ...errorTypes.required('Address'),
                    })}
                    error={errors.permanentAddress?.message as string}
                    label="Address"
                    id="Address"
                    placeholder="Enter address"
                    defaultValue={""}
                    required
                    disabled={isSameAsPermanentAddress}
                />
                <FormField
                    {...register('permanentState', {
                        ...errorTypes.required('State'),
                    })}
                    error={errors.permanentState?.message as string}
                    type="select"
                    label="State"
                    value={watch('permanentState')}
                    placeholder="Enter state"
                    options={states}
                    required
                    disabled={isSameAsPermanentAddress}
                />
                <FormField
                    {...register('permanentCity', {
                        ...errorTypes.required('City'),
                    })}
                    error={errors.permanentCity?.message as string}
                    label="City"
                    id="City"
                    placeholder="Enter city"
                    disabled={isSameAsPermanentAddress}
                />
                <FormField
                    {...register('permanentPinCode', {
                        ...errorTypes.required('Pin code'),
                        ...errorTypes.pinCode,
                    })}
                    error={errors.permanentPinCode?.message as string}
                    label="Pin Code"
                    id="Pincode"
                    placeholder="Enter pin code"
                    disabled={isSameAsPermanentAddress}
                />
            </div>
            <div className="flex items-center justify-end mt-6">
                <Button
                    type="button"
                    onClick={() => handleNextStep('tab5', ['address', 'state', 'city', 'pinCode', 'permanentAddress', 'permanentState', 'permanentCity', 'permanentPinCode'])}
                >
                    {isLoading ? (
                        <>
                            <Spinner />
                        </>
                    ) : (
                        'Save and Continue'
                    )}
                </Button>
            </div>
        </>
    )
}

export default OnboardAddress
